import { createApi } from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import { withLmaSafeApiLogging, type LmaHttpMethod } from '../../lib/utils/lmaSafeLogging';

/** Shown when every CC lookup in a deduplication batch fails (and as UI fallback copy). */
export const DEDUPLICATION_BATCH_ALL_FAILED_MESSAGE =
  "Oops! A few records didn't load as expected. Please click the refresh icon to try again.\nIf it keeps happening, contact IT support.";

/** Normalizes audit-history lead id argument to a non-empty int list (comma-separated strings supported). */
export function normalizeLeadIdsArg(
  input: number | string | (number | string)[] | undefined | null
): number[] {
  if (input == null) return [];
  if (Array.isArray(input)) {
    return input
      .flatMap((v) => (typeof v === 'string' ? v.split(',') : [String(v)]))
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => !Number.isNaN(n));
  }
  if (typeof input === 'number') return Number.isFinite(input) ? [Math.trunc(input)] : [];
  return String(input)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => !Number.isNaN(n));
}

// Custom base query using axios directly
// Note: We cannot use axiosFetcher here because it imports logger,
// which creates a circular dependency with the store during test initialization
const axiosBaseQuery =
  () =>
  async ({ url, method = 'POST', data, params }: any) => {
    try {
      const normalizedMethod = String(method).toUpperCase() as LmaHttpMethod;
      const response = await withLmaSafeApiLogging(
        {
          operation: 'ccPatientApiBaseQuery',
          method: normalizedMethod,
          endpoint: url,
        },
        () =>
          normalizedMethod === 'POST'
            ? axios.post('/api/dap-api', {
                url,
                data,
              })
            : axios.get('/api/dap-api', { params: { url, ...params } })
      );
      return { data: response?.data };
    } catch (error: any) {
      return {
        error: {
          status: error?.response?.status || 500,
          data: error?.response?.data || error.message || 'Unknown error',
        },
      };
    }
  };

export const ccPatientApi = createApi({
  reducerPath: 'ccPatientApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['CCPatient', 'AuditHistory'],
  endpoints: (builder) => ({
    // Get CC Patient by ID
    getCCPatientById: builder.query({
      query: (ccId: string) => ({
        url: `/CCT/GetPatientByIdFromCC?ccId=${ccId}`,
        method: 'POST',
      }),
      providesTags: (_result, _error, ccId) => [{ type: 'CCPatient', id: ccId }],
      // Transform response to return the data directly
      transformResponse: (response: any) => response,
    }),

    // Fetch multiple patients at once (batch request)
    getCCPatientsByIds: builder.query({
      async queryFn(ccIds: string[], _queryApi, _extraOptions, fetchWithBQ) {
        const results = await Promise.allSettled(
          ccIds.map((ccId) =>
            fetchWithBQ({
              url: `/CCT/GetPatientByIdFromCC?ccId=${ccId}`,
              method: 'POST',
            })
          )
        );

        const data: Record<string, any> = {};
        const errors: string[] = [];

        results.forEach((result, index) => {
          const ccId = ccIds[index];
          if (result.status === 'fulfilled' && result.value.data) {
            data[ccId] = result.value.data;
          } else if (result.status === 'rejected') {
            errors.push(`Failed to fetch data for ccId: ${ccId}`);
          } else if (result.status === 'fulfilled' && result.value.error) {
            errors.push(`Error for ccId ${ccId}: ${result.value.error.data}`);
          }
        });

        if (errors.length > 0 && Object.keys(data).length === 0) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: DEDUPLICATION_BATCH_ALL_FAILED_MESSAGE,
            },
          };
        }

        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...Object.keys(result).map((ccId) => ({ type: 'CCPatient' as const, id: ccId })),
              { type: 'CCPatient', id: 'LIST' },
            ]
          : [{ type: 'CCPatient', id: 'LIST' }],
    }),

    getAuditHistory: builder.query({
      query: ({ leadIdsArg }: { leadIdsArg: number | number[]; tabKey?: string }) => {
        const leadIds = normalizeLeadIdsArg(leadIdsArg);
        const params = leadIds.reduce<Record<string, number>>((acc, id, i) => {
          acc[`leadIds[${i}]`] = id;
          return acc;
        }, {});
        return {
          url: `/LMA/GetAuditHistory`,
          method: 'GET',
          params,
        };
      },
      providesTags: (_result, _error, { leadIdsArg }) => [
        { type: 'AuditHistory', id: String(leadIdsArg) },
      ],
      transformResponse: (response: any) => response,
    }),

    // Get tracking parameters by enrollment ID (loaded on row expansion)
    getTrackingParametersByEnrollmentId: builder.query({
      query: (enrollmentId: string) => ({
        url: `/LMA/GetTrackingParametersByEnrollmentId`,
        method: 'GET',
        params: { enrollmentId },
      }),
      transformResponse: (response: any) => response,
    }),
  }),
});

export const {
  useGetCCPatientByIdQuery,
  useGetCCPatientsByIdsQuery,
  useGetAuditHistoryQuery,
  useGetTrackingParametersByEnrollmentIdQuery,
  useLazyGetTrackingParametersByEnrollmentIdQuery,
  usePrefetch,
} = ccPatientApi;
